import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { NewsItemModel } from '../../../../api-service/clients';
import { FormDialog } from '../../../../components/form-dialog';
import { LinkIcon } from '../../../../components/icons/link-icon/link-icon';
import { ReadonlyText } from '../../../../components/readonly-input/readonly-text';
import { Row } from '../../../../components/row/row';
import { Cell } from '../../../../components/cell/cell';
import { formatDateTime } from '../../../../utilities/date-helper';
import { useNewsItemAttachmentActions, useNewsItemAttachmentDownload } from './download-news-item-attachment';
import { PdfActionIconButtons } from '../../../../components/pdf-actions/pdf-action-icon-buttons';
import { isPdfFile } from '../../../../components/pdf-actions/use-pdf-actions';

export interface INewsItemDetailsDialogProps {
  newsItem: NewsItemModel;
  onClose: () => void;
}

export function NewsItemDetailsDialog(props: INewsItemDetailsDialogProps) {
  const { newsItem, onClose } = props;
  const downloadAttachment = useNewsItemAttachmentDownload();
  const attachmentActions = useNewsItemAttachmentActions();
  const attachments = newsItem.attachments ?? [];

  return (
    <FormDialog open title={newsItem.title} confirmButtonText="Luk" onConfirmClick={onClose} onClose={onClose} maxWidth="md" fullWidth>
      <Row>
        <Cell size="full">
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {newsItem.content}
          </Typography>
        </Cell>
      </Row>
      {newsItem.url && (
        <Row>
          <Cell size="full">
            <Link variant="body2" href={toHref(newsItem.url)} target="_blank" rel="noreferrer" sx={{ overflowWrap: 'anywhere' }}>
              <LinkIcon /> {newsItem.url}
            </Link>
          </Cell>
        </Row>
      )}
      {attachments.length > 0 && (
        <Row>
          <Cell size="full">
            {attachments.map(attachment =>
              attachmentActions ? (
                <Box key={attachment.id} display="flex" alignItems="center" gap={1}>
                  <Link component="button" variant="body2" onClick={() => attachmentActions.onDownload(newsItem.id, attachment)}>
                    {attachment.filename}
                  </Link>
                  <PdfActionIconButtons
                    isPdf={isPdfFile(attachment.mimetype, attachment.filename)}
                    disabled={attachmentActions.isFetching}
                    onOpen={() => attachmentActions.onOpen(newsItem.id, attachment)}
                    onDownload={() => attachmentActions.onDownload(newsItem.id, attachment)}
                  />
                </Box>
              ) : (
                <Link key={attachment.id} component="button" variant="body2" onClick={() => downloadAttachment(newsItem.id, attachment)} sx={{ display: 'block' }}>
                  {attachment.filename}
                </Link>
              )
            )}
          </Cell>
        </Row>
      )}
      <Row>
        <Cell size="half">
          <ReadonlyText label="Oprettet af" value={newsItem.author} />
        </Cell>
        <Cell size="half">
          <ReadonlyText label="Oprettet" value={formatDateTime(newsItem.creationDate)} />
        </Cell>
        <Cell size="half">
          <ReadonlyText label="Synlig fra" value={formatDateTime(newsItem.showFromDate)} />
        </Cell>
        <Cell size="half">
          <ReadonlyText label="Synlig til og med" value={newsItem.expirationDate ? formatDateTime(newsItem.expirationDate) : 'Ingen udløbsdato'} />
        </Cell>
      </Row>
    </FormDialog>
  );
}

// Caseworkers often type "www.example.dk"; without a scheme the browser would resolve it relative to Fasit.
function toHref(url: string) {
  const trimmed = url.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `//${trimmed}`;
}
